import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Card,
  Form,
  Button,
  Row,
  Col,
  Alert
} from 'react-bootstrap';
import { FaSave, FaTimes } from 'react-icons/fa';

const AddDoctor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);
  
  const [formData, setFormData] = useState({
    name: '',
    specialization: '',
    qualification: '',
    contactNumber: '',
    email: '',
    consultationFee: 1000,
    availability: {
      days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      timeFrom: '09:00',
      timeTo: '17:00'
    },
    isActive: true
  });
  
  const [loading, setLoading] = useState(isEditMode);
  const [error, setError] = useState(null);
  
  const { name, specialization, qualification, contactNumber, email, consultationFee, availability, isActive } = formData;
  
  useEffect(() => {
    if (isEditMode) {
      const fetchDoctor = async () => {
        try {
          const res = await axios.get(`/api/doctors.php?id=${id}`);
          setFormData({
            name: res.data.name || '',
            specialization: res.data.specialization || '',
            qualification: res.data.qualification || '',
            contactNumber: res.data.contactNumber || '',
            email: res.data.email || '',
            consultationFee: res.data.consultationFee || 1000,
            availability: {
              days: res.data.availability?.days || ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
              timeFrom: res.data.availability?.timeFrom || '09:00',
              timeTo: res.data.availability?.timeTo || '17:00'
            },
            isActive: res.data.isActive !== undefined ? res.data.isActive : true
          });
          setLoading(false);
        } catch (err) {
          setError('Failed to load doctor information');
          setLoading(false);
        }
      };
      fetchDoctor();
    }
  }, [id, isEditMode]);

  const onChange = e => {
    const { name, value, type } = e.target;
    
    if (name === 'consultationFee') {
      setFormData({ ...formData, [name]: parseFloat(value) || 0 });
    } else if (name === 'specialization' || name === 'qualification') {
      if (value.length <= 45) {
        setFormData({ ...formData, [name]: value });
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };
  
  const onChangeAvailability = e => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      availability: {
        ...availability,
        [name]: value
      }
    });
  };
  
  const handleDaySelection = day => {
    const updatedDays = [...availability.days];
    
    if (updatedDays.includes(day)) {
      const index = updatedDays.indexOf(day);
      updatedDays.splice(index, 1);
    } else {
      updatedDays.push(day);
    }
    
    setFormData({
      ...formData,
      availability: {
        ...availability,
        days: updatedDays
      }
    });
  };
  
  const onSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      if (!name || !specialization || !qualification || !contactNumber) {
        setError('Please fill all required fields');
        setLoading(false);
        return;
      }
      
      if (isEditMode) {
        await axios.put(`/api/doctors.php?id=${id}`, {...formData, id: id});
      } else {
        await axios.post('/api/doctors.php', formData);
      }
      
      setLoading(false);
      // Wait a bit before redirect to ensure API call completes
      setTimeout(() => {
        navigate('/doctors');
      }, 500);
    } catch (err) {
      setError(err.response?.data.msg || 'Something went wrong');
      setLoading(false);
    }
  };
  
  const daysOfWeek = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>{isEditMode ? `Edit Doctor: ${name}` : 'Add New Doctor'}</h2>
        <Link to="/doctors">
          <Button variant="outline-secondary">
            <FaTimes className="me-2" /> Cancel
          </Button>
        </Link>
      </div>
      
      <Card>
        <Card.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          
          <Form onSubmit={onSubmit}>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Doctor Name *</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={name}
                    onChange={onChange}
                    placeholder="Enter doctor name"
                    required
                  />
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Specialization * (Max 45 words)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="specialization"
                    value={specialization}
                    onChange={onChange}
                    placeholder="Enter specialization (Press Enter for new line)"
                    required
                  />
                  <Form.Text className="text-muted">
                    {specialization.length}/45 characters
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Qualification * (Max 45 words)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    name="qualification"
                    value={qualification}
                    onChange={onChange}
                    placeholder="Enter qualification (Press Enter for new line)"
                    required
                  />
                  <Form.Text className="text-muted">
                    {qualification.length}/45 characters
                  </Form.Text>
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Contact Number *</Form.Label>
                  <Form.Control
                    type="text"
                    name="contactNumber"
                    value={contactNumber}
                    onChange={onChange}
                    placeholder="Enter contact number"
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={email}
                onChange={onChange}
                placeholder="Enter email address"
              />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Consultation Fee (PKR) *</Form.Label>
              <Form.Control
                type="number"
                name="consultationFee"
                value={consultationFee}
                onChange={onChange}
                placeholder="Enter consultation fee"
                min="0"
                step="100"
                required
              />
              <Form.Text className="text-muted">
                This fee will be automatically added to bills when this doctor is selected
              </Form.Text>
            </Form.Group>
            
            <h5 className="mt-4 mb-3">Availability</h5>
            
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Working Days</Form.Label>
                  <div className="d-flex flex-wrap">
                    {daysOfWeek.map(day => (
                      <Form.Check
                        type="checkbox"
                        id={`day-${day}`}
                        label={day}
                        key={day}
                        className="me-3 mb-2"
                        checked={availability.days.includes(day)}
                        onChange={() => handleDaySelection(day)}
                      />
                    ))}
                  </div>
                </Form.Group>
              </Col>
              
              <Col md={6}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Time From</Form.Label>
                      <Form.Control
                        type="time"
                        name="timeFrom"
                        value={availability.timeFrom}
                        onChange={onChangeAvailability}
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Time To</Form.Label>
                      <Form.Control
                        type="time"
                        name="timeTo"
                        value={availability.timeTo}
                        onChange={onChangeAvailability}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </Col>
            </Row>
            
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="isActive"
                label="Active Doctor"
                checked={isActive}
                onChange={e => setFormData({ ...formData, isActive: e.target.checked })}
              />
            </Form.Group>
            
            <div className="d-grid">
              <Button
                variant="primary"
                type="submit"
                disabled={loading}
              >
                {loading ? 'Saving...' : (
                  <>
                    <FaSave className="me-2" /> {isEditMode ? 'Update Doctor' : 'Save Doctor'}
                  </>
                )}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </div>
  );
};

export default AddDoctor; 
